// Decyzon — one-time 18+ confirmation gate.
// Stored in localStorage; minimal, blocking modal shown once before first use.
// Routes that bypass the gate: legal/info pages and shared-decision permalinks.

import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { DecyzonLogo } from "@/components/brand/DecyzonLogo";

const STORAGE_KEY = "decyzon.age_confirmed.v1";

const BYPASS_PREFIXES = [
  "/privacy",
  "/terms",
  "/refunds",
  "/contact",
  "/unsubscribe",
  "/about",
  "/v/", // shared decisions remain readable without the gate
];

export default function AgeGate() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (BYPASS_PREFIXES.some((p) => pathname.startsWith(p))) return;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
    } catch {
      /* noop */
    }
  }, [pathname]);

  const confirm = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now() }));
    } catch {
      /* noop */
    }
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Age confirmation"
        >
          <motion.div
            initial={{ y: 12, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="w-full max-w-sm rounded-2xl border border-border/60 bg-card p-6 shadow-2xl"
          >
            <div className="flex justify-center mb-4">
              <DecyzonLogo context="modal" />
            </div>
            <h2 className="text-base font-display font-semibold text-foreground text-center">
              Confirm you are 18 or older
            </h2>
            <p className="text-xs font-body text-muted-foreground leading-relaxed mt-2 text-center">
              Decyzon is a decision-support tool for prediction markets and is intended for adults only.
              Nothing here is financial, legal, or gambling advice. Availability of underlying markets
              depends on your region.
            </p>
            <button
              onClick={confirm}
              className="mt-5 w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-body font-medium hover:opacity-90 transition-opacity"
            >
              I'm 18 or older — continue
            </button>
            <Link
              to="/terms"
              className="block mt-2 text-[11px] font-body text-muted-foreground hover:text-foreground text-center"
            >
              Read terms of use
            </Link>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
